import React, { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useGetCardDetailAndBalance } from '@/queries/card';
import { useHomeSliderContext } from '@/modules/home/contexts/homeSlider';
import CardBalanceSlideAmount from './components/CardBalanceSlideAmount';
import CardBalanceSlideCard from './components/CardBalanceSlideCard';
import CardBalanceSlideCurrency from './components/CardBalanceSlideCurrency';
import CardBalanceSlideTitle from './components/CardBalanceSlideTitle';
import { useIsMasked } from '@/hooks/isMasked';
import { IBalanceCardProps } from '../BalanceSlide.types';

export interface ICardBalanceSlideProps extends Partial<IBalanceCardProps> {
  isMaskable?: boolean;
  showBalance?: boolean;
  isDetailsButtonInvisible?: boolean;
}

const CardBalanceSlide: FC<ICardBalanceSlideProps> = (props) => {
  const { isMasked, setIsMasked } = useIsMasked();
  const [showAmount, setShowAmount] = useState(
    props?.showBalance ? true : false
  );
  const { slideIndex } = useHomeSliderContext();
  const router = useRouter();

  const {
    cardBalanceResult,
    isCardActivated,
    isLoading,
    isError,
    isCardDisabled,
    refetch,
    isFetching,
  } = useGetCardDetailAndBalance(
    {
      refetchOnWindowFocus: props?.showBalance ? true : false,
      enabled: props?.showBalance ? true : false, // disable this query from automatically running
    },
    props?.showBalance ? true : false
  );

  useEffect(() => {
    if (slideIndex === 1)
      refetch({
        shouldRefetchBalance: false,
      });
  }, [slideIndex]);

  const balance = cardBalanceResult?.data?.balance!;
  const isLoadingOrFetching = isNaN(balance) ? isFetching : isLoading;
  const commonProps = !isMasked
    ? { isLoading: isLoadingOrFetching, isError: isError }
    : {};

  const shouldShowDetailsButton =
    !props?.isDetailsButtonInvisible && isCardActivated && !isCardDisabled;

  const { buttonTitle, buttonAction } = (() => {
    if (!shouldShowDetailsButton) {
      return { buttonTitle: undefined, buttonAction: undefined };
    }
    return {
      buttonTitle: 'گردش کارت',
      buttonAction: () => router.push('/card/transactions'),
    };
  })();

  return (
    <CardBalanceSlideCard
      amount={
        <CardBalanceSlideAmount
          {...commonProps}
          amount={balance!}
          isCardDisabled={isCardDisabled}
          isError={isError}
          onRefetch={refetch}
        />
      }
      buttonAction={buttonAction}
      buttonTitle={buttonTitle}
      currency={<CardBalanceSlideCurrency {...commonProps} />}
      isCardDisabled={isCardDisabled}
      isError={isError}
      isFetching={isFetching}
      isIssued={isLoading || isError || isCardActivated}
      isMasked={showAmount ? isMasked : true}
      refetch={refetch}
      setShowAmount={setShowAmount}
      showAmount={showAmount}
      title={<CardBalanceSlideTitle {...commonProps} />}
      onMaskChange={(isMasked) => setIsMasked(isMasked)}
      {...props}
    />
  );
};

export default CardBalanceSlide;
